<script setup lang="ts">
/**
 * The one button.
 *
 * Two things here are not cosmetic:
 *
 *   - **The label color is `--text-on-accent`, never white.** DESIGN.md
 *     section 5: the accent inverts polarity between themes — near-white on dark
 *     green in light, near-black on light green in dark. A hardcoded white label
 *     passes light-theme review and is unreadable at 2am.
 *   - **`.mp-hit` on the small variant.** DESIGN.md section 8 lists the 30px
 *     small button as the one open accessibility defect. The visual box stays on
 *     the type scale; the *hit* area is grown to `--target-min` by the pseudo
 *     element in `styles/base.css`.
 */

import { computed } from 'vue'

import MpIcon from './MpIcon.vue'
import type { IconName } from './icons'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'critical'
    size?: 'md' | 'sm'
    type?: 'button' | 'submit'
    disabled?: boolean
    busy?: boolean
    icon?: IconName | undefined
    /** Required when the button renders an icon and no text. */
    iconOnlyLabel?: string | undefined
  }>(),
  { variant: 'secondary', size: 'md', type: 'button', disabled: false, busy: false }
)

const isDisabled = computed(() => props.disabled || props.busy)
</script>

<template>
  <button
    class="mp-button mp-hit"
    :class="[`mp-button--${props.variant}`, `mp-button--${props.size}`, { 'mp-button--icon-only': props.iconOnlyLabel }]"
    :type="props.type"
    :disabled="isDisabled"
    :aria-busy="props.busy || undefined"
    :aria-label="props.iconOnlyLabel"
  >
    <MpIcon
      v-if="props.icon"
      :name="props.icon"
      :size="props.size === 'sm' ? 'sm' : 'md'"
    />
    <span
      v-if="!props.iconOnlyLabel"
      class="mp-button__label"
    ><slot /></span>
  </button>
</template>

<style scoped>
.mp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-block-size: var(--control-h);
  padding-inline: var(--space-4);
  border: 1px solid transparent;
  border-radius: var(--radius-control);
  font-family: var(--font-body);
  font-size: var(--font-size-body);
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--motion-ease),
    border-color var(--motion-fast) var(--motion-ease),
    color var(--motion-fast) var(--motion-ease);
}

.mp-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.mp-button[disabled] {
  cursor: not-allowed;
  opacity: 0.55;
}

/* The small variant keeps a shorter visual box and inherits the 44px hit area
   from .mp-hit. Font size steps down the scale rather than to a raw px value. */
.mp-button--sm {
  min-block-size: var(--space-8);
  padding-inline: var(--space-3);
  font-size: var(--font-size-small);
}

.mp-button--icon-only {
  padding-inline: 0;
  inline-size: var(--target-min);
  min-block-size: var(--target-min);
}

.mp-button--primary {
  background: var(--accent);
  color: var(--text-on-accent);
}

.mp-button--primary:hover:not([disabled]) {
  background: var(--accent-hover);
}

.mp-button--secondary {
  background: var(--surface-raised);
  color: var(--text-primary);
  border-color: var(--border-control);
}

.mp-button--secondary:hover:not([disabled]) {
  background: var(--surface-sunken);
}

.mp-button--ghost {
  background: transparent;
  color: var(--accent);
}

.mp-button--ghost:hover:not([disabled]) {
  background: var(--accent-subtle-bg);
  color: var(--accent-subtle-text);
}

.mp-button--critical {
  background: var(--surface-raised);
  color: var(--critical-text);
  border-color: var(--critical-text);
}

.mp-button--critical:hover:not([disabled]) {
  background: var(--critical-bg);
}

.mp-button__label {
  /* Long labels wrap rather than overflow: DESIGN.md section 8 warns against
     layouts that assume English string lengths. */
  text-align: center;
}
</style>
